import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { currentCalendarYear } from '@/lib/calendarYear';
import {
  clearPendingDonation,
  readDonationNonce,
  readPendingDonation,
  stripSessionIdFromUrl,
  track,
} from '@/lib/analytics';

const DonationThanks = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [email, setEmail] = useState<string | null>(null);
  // Starts false even with a session_id. The build prerenders this page with
  // no query string, so the HTML has no spinner, and the first client render
  // must match it or React throws #418 (ERI-2839). The effect turns it on.
  const [emailLoading, setEmailLoading] = useState(false);
  const [newsletterState, setNewsletterState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  useEffect(() => {
    if (!sessionId) return;

    // get-session-email answers only the browser that started checkout, which
    // holds this nonce (ERI-2782). Without it, skip the newsletter offer.
    const nonce = readDonationNonce(sessionId);
    if (!nonce) return;

    setEmailLoading(true);
    const fetchEmail = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-session-email', {
          body: { sessionId, nonce },
        });
        if (!error && data?.email) {
          setEmail(data.email);
          const pending = readPendingDonation(sessionId);
          try {
            track('donation', pending ?? {}, { onceKey: `donation:${sessionId}` });
          } catch {
            // A tracking failure must not skip the thanks page.
          }
          clearPendingDonation(sessionId);
          stripSessionIdFromUrl();
        }
      } catch {
        // Gracefully degrade — still show thanks. Leave session_id so a refresh can retry.
      } finally {
        setEmailLoading(false);
      }
    };

    fetchEmail();
  }, [sessionId]);

  const handleNewsletter = async () => {
    if (!email) return;
    setNewsletterState('loading');
    try {
      const { error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: { email, source: 'donation' },
      });
      if (error) {
        setNewsletterState('error');
      } else {
        setNewsletterState('done');
        track('newsletter_signup');
      }
    } catch {
      setNewsletterState('error');
    }
  };

  return (
    <>
      <SEOHead
        title="Thank You | Milky Way Planner"
        description="Thank you for supporting Milky Way Planner. Your donation keeps the free Milky Way calendar, moon tools, and location pages available."
        noindex
      />

      <div className="min-h-screen bg-background">
        <section className="max-w-xl mx-auto px-6 pt-20 pb-16 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground tracking-[-0.03em] mb-4">
            Thank you for your support
          </h1>
          <p className="text-muted-foreground leading-relaxed mb-10">
            Your contribution helps keep the Milky Way calendar free and ad-free
            for photographers everywhere.
          </p>

          {/* Newsletter opt-in */}
          {emailLoading ? (
            <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm mb-10">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : email && newsletterState !== 'done' ? (
            <div className="mb-10">
              <p className="text-sm text-muted-foreground mb-3">
                Want to hear about new features and Milky Way season updates?
              </p>
              <Button
                onClick={handleNewsletter}
                disabled={newsletterState === 'loading'}
                variant="outline"
                size="sm"
              >
                {newsletterState === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Subscribe as {email}
              </Button>
              {newsletterState === 'error' && (
                <p className="text-xs text-red-400 mt-2">Something went wrong. Try again?</p>
              )}
            </div>
          ) : newsletterState === 'done' ? (
            <div className="flex items-center justify-center gap-2 text-gold mb-10">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">Subscribed</span>
            </div>
          ) : null}

          <Link
            to={`/milky-way-calendar/${currentCalendarYear()}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-gold hover:text-gold-light transition-colors"
          >
            Back to the calendar
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </>
  );
};

export default DonationThanks;
